/**
 * SPA page-view + route-performance tracking.
 *
 * Uses `afterEach` (navigation already committed) for page views — NEVER
 * `beforeEach` for anything that could block — and an extra `beforeEach` that
 * only stamps a timestamp and ALWAYS calls next(), so it can never block, delay
 * or cancel a route. Everything is wrapped in try/catch.
 */
import { track } from './tracking'

function nowMs() {
  try {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : new Date().getTime()
  } catch (e) {
    return null
  }
}

// Run cb after the browser has painted the new route (double rAF ≈ post-paint).
function afterPaint(cb) {
  try {
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(cb))
    } else {
      setTimeout(cb, 0)
    }
  } catch (e) {
    /* swallow */
  }
}

export function installRouterTracking(router) {
  try {
    if (!router || typeof router.afterEach !== 'function') return

    let first = true
    let navStart = null

    // Stamp navigation start. The try/catch guards only the stamp; next() is
    // ALWAYS called, so this guard can never block navigation.
    if (typeof router.beforeEach === 'function') {
      router.beforeEach((to, from, next) => {
        try {
          navStart = nowMs()
        } catch (e) {
          /* ignore */
        }
        next()
      })
    }

    router.afterEach((to) => {
      try {
        const start = navStart
        navStart = null

        // public/index.html already fires the initial trackPageView on hard
        // load; skip the first afterEach so the landing page is not double-counted.
        if (first) {
          first = false
          return
        }

        const title = (to && (to.name || to.path)) || undefined
        const url = typeof window !== 'undefined' ? window.location.href : undefined
        track.pageView(url, title)

        // Route-change render time vs the 2s budget, measured after paint.
        // Never blocks and never affects the pageview above.
        if (start != null) {
          afterPaint(() => {
            try {
              const end = nowMs()
              if (end != null) track.perf(end - start)
            } catch (e) {
              /* ignore */
            }
          })
        }
      } catch (e) {
        /* swallow — never impact routing */
      }
    })
  } catch (e) {
    /* swallow */
  }
}
